/**
 * sl-8n5m.9.3: RelatedEntries ViewComponent
 *
 * Renders related rows based on matchBy-field overlap. Silent hide when no
 * matches — the empty state is intentionally invisible so detail pages
 * without related content don't show a useless "no results" box.
 */
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type {
  BoundDataRow,
  ViewComponent,
  ViewRuntimeContext,
} from '../../../types/view-component.types';
import { PlaceholderWarning } from '../../app-base/placeholder-warning';
import {
  resolveResponsiveImage,
  ResponsiveImage,
} from '../../responsive-image/responsive-image';
import * as styles from './related-entries.css';

const configSchema = z.object({
  matchByField: z.string().default('matchBy'),
  titleField: z.string().default('title'),
  imageField: z.string().default('image'),
  altField: z.string().default('alt'),
  slugField: z.string().default('slug'),
  currentSlug: z.string().default(''),
  maxItems: z.number().int().min(1).max(20).default(4),
  linkBase: z.string().default(''),
});

function extractValues(cell: unknown): string[] {
  if (cell == null) return [];
  if (Array.isArray(cell)) return cell.map(v => String(v).toLowerCase());
  if (typeof cell === 'string') {
    return cell
      .split(',')
      .map(v => v.trim().toLowerCase())
      .filter(Boolean);
  }
  if (typeof cell === 'object' && 'id' in (cell as Record<string, unknown>)) {
    // relation cell: { id: string, ... }
    return [String((cell as { id: string }).id).toLowerCase()];
  }
  return [];
}

function readTextCell(row: BoundDataRow, field: string): string {
  const value = row[field];
  return typeof value === 'string' ? value.trim() : '';
}

export const RelatedEntriesComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.RELATED_ENTRIES,
  type: 'content',
  name: 'Related Entries',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-app-font-size-sm)' } },
    '🔗'
  ),
  capabilities: [
    {
      role: 'related',
      description:
        'List of related rows matched by overlap on a tag/select/relation column',
      required: true,
      requirements: [
        { field: 'matchBy', acceptedTypes: ['text'], required: true },
        { field: 'title', acceptedTypes: ['text'], required: true },
        {
          field: 'image',
          acceptedTypes: ['attachment', 'url'],
          required: false,
        },
        { field: 'alt', acceptedTypes: ['text'], required: false },
        { field: 'slug', acceptedTypes: ['text'], required: false },
      ],
    },
  ],
  configSchema,
  defaultConfig: {
    matchByField: 'matchBy',
    titleField: 'title',
    imageField: 'image',
    altField: 'alt',
    slugField: 'slug',
    currentSlug: '',
    maxItems: 4,
    linkBase: '',
  },
  render({ config, data, runtimeContext }) {
    const cfg = config as z.infer<typeof configSchema>;
    const altField = cfg.altField ?? 'alt';
    const workspaceId = readWorkspaceId(runtimeContext);

    // Find the current row inside data (matched by slug) to seed the match vector.
    const currentRow = cfg.currentSlug
      ? data.find(r => String(r[cfg.slugField] ?? '') === cfg.currentSlug)
      : undefined;

    if (!currentRow) {
      // No anchor row — cannot compute overlap. Surface placeholder only if
      // consumer explicitly asked for related (role is required), otherwise
      // silent hide. We return Placeholder since role is required.
      return createElement(PlaceholderWarning, {
        reasonKey: 'relatedCurrentRowMissing',
        role: 'related',
      });
    }

    const currentValues = extractValues(currentRow[cfg.matchByField]);
    if (currentValues.length === 0) {
      return null; // silent hide: anchor row has no tags to match against
    }
    const currentSet = new Set(currentValues);

    const scored: Array<{ row: BoundDataRow; score: number }> = [];
    for (const row of data) {
      if (row === currentRow) continue;
      const rowValues = extractValues(row[cfg.matchByField]);
      const overlap = rowValues.filter(v => currentSet.has(v)).length;
      if (overlap > 0) scored.push({ row, score: overlap });
    }
    if (scored.length === 0) return null; // silent hide: no matches

    scored.sort((a, b) => b.score - a.score);
    const top = scored.slice(0, cfg.maxItems);

    return createElement(
      'div',
      {
        className: styles.list,
      },
      ...top.map((entry, idx) => {
        const title = String(entry.row[cfg.titleField] ?? 'Untitled');
        const image = resolveResponsiveImage(entry.row[cfg.imageField], {
          workspaceId,
          allowWorkspaceBlobFallback: Boolean(workspaceId),
        });
        const alt = readTextCell(entry.row, altField);
        const slug = String(entry.row[cfg.slugField] ?? '');
        const href = slug ? `${cfg.linkBase}${slug}` : undefined;

        const imageEl =
          image &&
          createElement(
            'div',
            { className: styles.itemImage },
            createElement(ResponsiveImage, {
              image: entry.row[cfg.imageField],
              role: 'related',
              alt,
              workspaceId,
              allowWorkspaceBlobFallback: Boolean(workspaceId),
              className: styles.itemImageFill,
            })
          );
        const titleEl = createElement(
          'div',
          { className: styles.itemTitle, 'data-sl-field': 'title' },
          title
        );

        return createElement(
          href ? 'a' : 'div',
          {
            key: slug || idx,
            className: styles.item,
            ...(href ? { href } : {}),
            'data-sl-field': 'related-item',
          },
          imageEl,
          titleEl
        );
      })
    );
  },
  palettePreview() {
    return createElement(
      'div',
      {
        style: {
          padding: 'var(--sl-app-padding-sm)',
          fontSize: 'var(--sl-app-font-size-xs)',
          color: 'var(--streamlain-v2-text-secondary)',
        },
      },
      '🔗 Related'
    );
  },
};

function readWorkspaceId(
  runtimeContext?: ViewRuntimeContext
): string | undefined {
  const value = runtimeContext?.appBase?.['workspaceId'];
  return typeof value === 'string' && value.trim() !== ''
    ? value.trim()
    : undefined;
}
